import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Calling from '../../api/Calling';
import { TextField, InputAdornment, IconButton, Select, MenuItem, CircularProgress, Skeleton } from '@mui/material';
import {
    Plus, Pencil, Trash2, Search, X, Check,
    ChevronUp, ChevronDown, Clock, Package, Tag, TrendingUp,
    Star, Zap, Award, RefreshCw, LayoutGrid, List, Percent,
    IndianRupee, Hash, Sparkles, CalendarClock
} from 'lucide-react';
import generateCaptcha from '../../components/captcha';
import formatDate from '../../components/formatDate';
import TableSkeleton from '../../components/ul/TableSkeleton';

// Shared MUI light-field styling
const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' },
};

const Plan = () => {
    const [loader, setLoader] = useState(true);
    const [planData, setPlanData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [viewMode, setViewMode] = useState('table');
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [formData, setFormData] = useState({
        p_name: '', p_description: '', p_main_price: '', p_price: '', p_duration: '', p_label: '', p_status: true
    });
    const [editId, setEditId] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchPlanData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getPlan();
            if (response?.status) {
                setPlanData(response?.data || []);
            } else {
                toast.error(response?.message || 'Failed to fetch plan data');
            }
        } catch (error) {
            toast.error('Failed to fetch plan data');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => { fetchPlanData(); }, []);

    useEffect(() => {
        let result = [...planData];
        if (searchQuery.trim() !== '') {
            result = result.filter(item =>
                item.p_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.p_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.p_label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.p_price?.toString().includes(searchQuery)
            );
        }
        result.sort((a, b) => {
            let aValue = a[sortField], bValue = b[sortField];
            if (['p_name', 'p_description', 'p_label'].includes(sortField)) {
                aValue = (aValue || '').toLowerCase();
                bValue = (bValue || '').toLowerCase();
            }
            if (['p_main_price', 'p_price', 'p_duration', 'p_per_day'].includes(sortField)) {
                aValue = Number(aValue) || 0;
                bValue = Number(bValue) || 0;
            }
            if (sortField === 'createdAt') {
                aValue = new Date(aValue || 0).getTime();
                bValue = new Date(bValue || 0).getTime();
            }
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        setFilteredData(result);
    }, [searchQuery, planData, sortField, sortOrder]);

    const handleSort = (field) => {
        if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortOrder('asc'); }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.p_name.trim()) errors.p_name = 'Plan name is required';
        else if (formData.p_name.length < 2) errors.p_name = 'Min 2 characters';
        if (!formData.p_main_price.toString().trim()) errors.p_main_price = 'MRP is required';
        else if (isNaN(formData.p_main_price) || Number(formData.p_main_price) < 0) errors.p_main_price = 'Invalid price';
        if (!formData.p_price.toString().trim()) errors.p_price = 'Offer price required';
        else if (isNaN(formData.p_price) || Number(formData.p_price) < 0) errors.p_price = 'Invalid price';
        else if (Number(formData.p_price) > Number(formData.p_main_price)) errors.p_price = 'Cannot exceed MRP';
        if (!formData.p_duration.toString().trim()) errors.p_duration = 'Duration required';
        else if (isNaN(formData.p_duration) || Number(formData.p_duration) <= 0) errors.p_duration = 'Invalid duration';
        if (!formData.p_label.trim()) errors.p_label = 'Label is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') setFormData(prev => ({ ...prev, [name]: checked }));
        else if (['p_main_price', 'p_price', 'p_duration'].includes(name)) {
            setFormData(prev => ({ ...prev, [name]: value.replace(/[^0-9.]/g, '') }));
        } else setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleStatusToggle = () => setFormData(prev => ({ ...prev, p_status: !prev.p_status }));

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            const body = {
                p_name: formData.p_name.trim(), p_description: formData.p_description.trim(),
                p_main_price: Number(formData.p_main_price), p_price: Number(formData.p_price),
                p_duration: Number(formData.p_duration), p_label: formData.p_label.trim(), p_status: formData.p_status
            };
            const response = await Calling.createPlan(body);
            if (response?.status) {
                toast.success(response?.message || 'Plan created!');
                setShowAddModal(false); resetForm(); fetchPlanData();
            } else toast.error(response?.message || 'Failed');
        } catch { toast.error('Failed to create plan'); }
        finally { setIsSubmitting(false); }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            const body = {
                p_name: formData.p_name.trim(), p_description: formData.p_description.trim(),
                p_main_price: Number(formData.p_main_price), p_price: Number(formData.p_price),
                p_duration: Number(formData.p_duration), p_label: formData.p_label.trim(), p_status: formData.p_status
            };
            const response = await Calling.updatePlan(editId, body);
            if (response?.status) {
                toast.success(response?.message || 'Plan updated!');
                setShowEditModal(false); resetForm(); fetchPlanData();
            } else toast.error(response?.message || 'Failed');
        } catch { toast.error('Failed to update plan'); }
        finally { setIsSubmitting(false); }
    };

    const handleDeleteSingle = async (id) => {
        const check = generateCaptcha('You are about to delete this plan. Solve this:');
        if (!check) return toast.error('Incorrect answer', { autoClose: 2000 });
        setLoader(true);
        try {
            const response = await Calling.deletePlan(id);
            if (response?.status) { toast.success(response?.message || 'Deleted!'); fetchPlanData(); }
            else { toast.error(response?.message || 'Failed'); setLoader(false); }
        } catch { toast.error('Failed to delete'); setLoader(false); }
    };

    const openEditModal = (item) => {
        setFormData({
            p_name: item.p_name || '', p_description: item.p_description || '',
            p_main_price: item.p_main_price?.toString() || '', p_price: item.p_price?.toString() || '',
            p_duration: item.p_duration?.toString() || '', p_label: item.p_label || '', p_status: item.p_status ?? true
        });
        setEditId(item._id);
        setShowEditModal(true);
    };

    const resetForm = () => {
        setFormData({ p_name: '', p_description: '', p_main_price: '', p_price: '', p_duration: '', p_label: '', p_status: true });
        setFormErrors({}); setEditId(null);
    };

    const formatPrice = (price) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price || 0);
    const formatPriceDecimal = (price) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price || 0);

    const calculateDiscount = (main, offer) => {
        const m = Number(main) || 0, o = Number(offer) || 0;
        if (m <= 0 || o <= 0 || o >= m) return 0;
        return Math.round(((m - o) / m) * 100);
    };

    const calculateSavePrice = (main, offer) => {
        const m = Number(main) || 0, o = Number(offer) || 0;
        if (m <= 0 || o <= 0 || o >= m) return 0;
        return m - o;
    };

    const calculatePerDay = (price, duration) => {
        const p = Number(price) || 0, d = Number(duration) || 0;
        if (p <= 0 || d <= 0) return 0;
        return (p / d).toFixed(2);
    };

    const getLabelIcon = (label) => {
        const l = label?.toLowerCase() || '';
        if (l.includes('popular') || l.includes('best')) return Star;
        if (l.includes('premium') || l.includes('pro')) return Award;
        if (l.includes('basic') || l.includes('starter')) return Zap;
        return Tag;
    };

    const getLabelStyle = (label) => {
        const l = label?.toLowerCase() || '';
        if (l.includes('popular') || l.includes('best')) return 'bg-main-yellow/15 text-main-yellow border-main-yellow/30';
        if (l.includes('premium') || l.includes('pro')) return 'bg-main-exBlue/10 text-main-exBlue border-main-exBlue/30';
        if (l.includes('basic') || l.includes('starter')) return 'bg-main-blue/10 text-main-blue border-main-blue/30';
        return 'bg-main-subfont/10 text-main-subfont border-main-subfont/30';
    };

    const stats = {
        total: planData.length,
        active: planData.filter(p => p.p_status).length,
        inactive: planData.filter(p => !p.p_status).length,
        avgDiscount: planData.length > 0 ? Math.round(planData.reduce((a, p) => a + calculateDiscount(p.p_main_price, p.p_price), 0) / planData.length) : 0
    };

    // Loading State (MUI Skeleton mirroring the page) — only on the initial load (no data yet).
    // Swapping the whole tree on every refresh while the AnimatePresence modal is mid-exit
    // is what triggered the "removeChild" crash, so keep the real tree mounted once data exists.
    if (loader && planData.length === 0) {
        return (
            <div className="w-full text-main-font">
                {/* Header skeleton */}
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-6 gap-4">
                    <div className="flex items-center gap-4">
                        <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '12px' }} />
                        <div>
                            <Skeleton variant="text" width={220} height={30} />
                            <Skeleton variant="text" width={260} height={18} />
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: '8px' }} />
                        <Skeleton variant="rounded" width={130} height={42} sx={{ borderRadius: '8px' }} />
                    </div>
                </div>

                {/* Stats skeleton */}
                <div className="bg-main-white rounded-xl border border-main-border p-5 mb-6">
                    <Skeleton variant="text" width={90} height={18} sx={{ mb: 2 }} />
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, idx) => (
                            <Skeleton key={idx} variant="rounded" height={76} sx={{ borderRadius: '8px' }} />
                        ))}
                    </div>
                </div>

                {/* Search bar skeleton */}
                <div className="bg-main-white rounded-xl border border-main-border p-5 mb-6">
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        <Skeleton variant="rounded" height={44} sx={{ borderRadius: '12px', flex: 1, width: '100%', maxWidth: 420 }} />
                        <div className="flex items-center gap-3">
                            <Skeleton variant="rounded" width={92} height={42} sx={{ borderRadius: '8px' }} />
                            <Skeleton variant="rounded" width={170} height={42} sx={{ borderRadius: '8px' }} />
                        </div>
                    </div>
                </div>

                {/* Table skeleton */}
                <div className="bg-main-white rounded-xl border border-main-border overflow-hidden">
                    <TableSkeleton rows={6} cols={7} />
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full text-main-font"
        >
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-main-blue rounded-xl flex items-center justify-center shadow-lg shadow-main-blue/30">
                        <Package size={24} className="text-main-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">Plans Management</h1>
                        <p className="text-sm text-main-subfont mt-0.5">Create and manage subscription plans</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={fetchPlanData} className="w-10 h-10 border border-main-border text-main-subfont rounded-lg hover:bg-main-th hover:text-main-blue hover:border-main-blue/30 transition-all flex items-center justify-center" title="Refresh">
                        <RefreshCw size={18} />
                    </button>
                    <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 bg-main-blue text-main-white px-5 py-2.5 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-main-blue/30 active:scale-[0.98]">
                        <Plus size={18} strokeWidth={2.5} />
                        <span>Add Plan</span>
                    </button>
                </div>
            </div>

            {/* Stats Section */}
            <div className="bg-main-white rounded-xl border border-main-border p-5 mb-6">
                <h2 className="text-sm font-semibold text-main-subfont uppercase tracking-wider mb-4">Overview</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard title="Total Plans" value={stats.total} icon={Package} colorClass="bg-main-exBlue" />
                    <StatCard title="Active Plans" value={stats.active} icon={Check} colorClass="bg-main-blue" />
                    <StatCard title="Inactive" value={stats.inactive} icon={X} colorClass="bg-main-red" />
                    <StatCard title="Avg. Discount" value={`${stats.avgDiscount}%`} icon={Percent} colorClass="bg-main-yellow" />
                </div>
            </div>

            {/* Search & Controls */}
            <div className="bg-main-white rounded-xl border border-main-border p-5 mb-6">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative flex-1 w-full md:max-w-md">
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search plans by name, label or price..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={fieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={18} className="text-main-subfont" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="clear search">
                                                <X size={16} className="text-main-subfont" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                },
                            }}
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center bg-main-bg border border-main-border rounded-lg p-1">
                            <button onClick={() => setViewMode('table')} className={`p-2 rounded-md transition-all ${viewMode === 'table' ? 'bg-main-white shadow-sm text-main-blue' : 'text-main-subfont hover:text-main-font'}`}>
                                <List size={18} />
                            </button>
                            <button onClick={() => setViewMode('card')} className={`p-2 rounded-md transition-all ${viewMode === 'card' ? 'bg-main-white shadow-sm text-main-blue' : 'text-main-subfont hover:text-main-font'}`}>
                                <LayoutGrid size={18} />
                            </button>
                        </div>
                        <Select
                            value={`${sortField}-${sortOrder}`}
                            onChange={(e) => { const [f, o] = e.target.value.split('-'); setSortField(f); setSortOrder(o); }}
                            size="small"
                            sx={{
                                borderRadius: '12px',
                                backgroundColor: '#fff',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                minWidth: 168,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--main-border, #e5e7eb)' },
                            }}
                        >
                            <MenuItem value="p_name-asc">Name A-Z</MenuItem>
                            <MenuItem value="p_name-desc">Name Z-A</MenuItem>
                            <MenuItem value="p_price-asc">Price Low to High</MenuItem>
                            <MenuItem value="p_price-desc">Price High to Low</MenuItem>
                            <MenuItem value="p_per_day-asc">Per Day ↑</MenuItem>
                            <MenuItem value="p_per_day-desc">Per Day ↓</MenuItem>
                            <MenuItem value="createdAt-desc">Newest First</MenuItem>
                            <MenuItem value="createdAt-asc">Oldest First</MenuItem>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="bg-main-white rounded-xl border border-main-border overflow-hidden">
                {filteredData.length === 0 ? (
                    <EmptyState searchQuery={searchQuery} onAddClick={() => setShowAddModal(true)} onClear={() => setSearchQuery('')} />
                ) : viewMode === 'card' ? (
                    <div className="p-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                            {filteredData.map((item) => (
                                <PlanCard key={item._id} item={item} formatPrice={formatPrice} formatPriceDecimal={formatPriceDecimal} calcDiscount={calculateDiscount} calcSave={calculateSavePrice} getLabelIcon={getLabelIcon} getLabelStyle={getLabelStyle} onEdit={openEditModal} onDelete={handleDeleteSingle} />
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="hidden lg:grid lg:grid-cols-12 gap-4 px-5 py-3.5 bg-main-th text-xs font-semibold text-main-subfont uppercase tracking-wider border-b border-main-border">
                            <div className="col-span-1">#</div>
                            <div className="col-span-2"><SortableHeader field="p_name" label="Plan" handleSort={handleSort} sortField={sortField} sortOrder={sortOrder} /></div>
                            <div className="col-span-2">Pricing</div>
                            <div className="col-span-1"><SortableHeader field="p_per_day" label="Per Day" handleSort={handleSort} sortField={sortField} sortOrder={sortOrder} /></div>
                            <div className="col-span-1"><SortableHeader field="p_duration" label="Duration" handleSort={handleSort} sortField={sortField} sortOrder={sortOrder} /></div>
                            <div className="col-span-2">Label</div>
                            <div className="col-span-1">Status</div>
                            <div className="col-span-1"><SortableHeader field="createdAt" label="Date" handleSort={handleSort} sortField={sortField} sortOrder={sortOrder} /></div>
                            <div className="col-span-1 text-right">Actions</div>
                        </div>
                        <div className="divide-y divide-main-border/50">
                            {filteredData.map((item, idx) => (
                                <TableRow key={item._id} item={item} index={idx} formatPrice={formatPrice} formatPriceDecimal={formatPriceDecimal} calcDiscount={calculateDiscount} calcSave={calculateSavePrice} getLabelIcon={getLabelIcon} getLabelStyle={getLabelStyle} onEdit={openEditModal} onDelete={handleDeleteSingle} />
                            ))}
                        </div>
                    </>
                )}
                {filteredData.length > 0 && (
                    <div className="px-5 py-4 border-t border-main-border bg-main-bg/50">
                        <p className="text-sm text-main-subfont text-center">
                            Showing <span className="font-semibold text-main-font">{filteredData.length}</span> of <span className="font-semibold text-main-font">{planData.length}</span> plans
                        </p>
                    </div>
                )}
            </div>

            {/* Modals */}
            <Modal show={showAddModal} onClose={() => { setShowAddModal(false); resetForm(); }} title="Create New Plan" icon={Plus}>
                <PlanForm formData={formData} formErrors={formErrors} isSubmitting={isSubmitting} onChange={handleInputChange} onStatusToggle={handleStatusToggle} onSubmit={handleAdd} onCancel={() => { setShowAddModal(false); resetForm(); }} submitLabel="Create Plan" formatPrice={formatPrice} formatPriceDecimal={formatPriceDecimal} calcDiscount={calculateDiscount} calcSave={calculateSavePrice} calcPerDay={calculatePerDay} />
            </Modal>
            <Modal show={showEditModal} onClose={() => { setShowEditModal(false); resetForm(); }} title="Edit Plan" icon={Pencil}>
                <PlanForm formData={formData} formErrors={formErrors} isSubmitting={isSubmitting} onChange={handleInputChange} onStatusToggle={handleStatusToggle} onSubmit={handleEdit} onCancel={() => { setShowEditModal(false); resetForm(); }} submitLabel="Update Plan" formatPrice={formatPrice} formatPriceDecimal={formatPriceDecimal} calcDiscount={calculateDiscount} calcSave={calculateSavePrice} calcPerDay={calculatePerDay} />
            </Modal>
        </motion.div>
    );
};

export default Plan;

// ==================== SUB-COMPONENTS ====================

const StatCard = ({ title, value, icon: Icon, colorClass }) => (
    <div className="flex items-center gap-4 p-4 bg-main-bg rounded-lg border border-main-border/50 hover:border-main-blue/30 hover:shadow-md transition-all">
        <div className={`w-11 h-11 ${colorClass} rounded-lg flex items-center justify-center shadow-md`}>
            <Icon size={20} className="text-main-white" />
        </div>
        <div>
            <p className="text-xs text-main-subfont font-medium uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-main-font mt-0.5">{value}</p>
        </div>
    </div>
);

const EmptyState = ({ searchQuery, onAddClick, onClear }) => (
    <div className="p-12 text-center">
        <div className="w-16 h-16 bg-main-bg border border-main-border rounded-xl flex items-center justify-center mx-auto mb-4">
            <Package size={28} className="text-main-subfont" />
        </div>
        <h3 className="text-lg font-semibold text-main-font">{searchQuery ? 'No plans found' : 'No plans yet'}</h3>
        <p className="text-main-subfont mt-1 mb-5 max-w-sm mx-auto text-sm">{searchQuery ? 'Try different search keywords or clear the filter' : 'Get started by creating your first subscription plan'}</p>
        {searchQuery ? (
            <button onClick={onClear} className="text-main-blue font-semibold hover:underline text-sm">Clear search</button>
        ) : (
            <button onClick={onAddClick} className="inline-flex items-center gap-2 bg-main-blue text-main-white px-5 py-2.5 rounded-lg font-semibold hover:shadow-lg hover:shadow-main-blue/30 transition-all text-sm">
                <Plus size={16} strokeWidth={2.5} />Create First Plan
            </button>
        )}
    </div>
);

const PlanCard = ({ item, formatPrice, formatPriceDecimal, calcDiscount, calcSave, getLabelIcon, getLabelStyle, onEdit, onDelete }) => {
    const discount = calcDiscount(item.p_main_price, item.p_price);
    const save = calcSave(item.p_main_price, item.p_price);
    const perDay = item.p_per_day || (item.p_price && item.p_duration ? (item.p_price / item.p_duration).toFixed(2) : 0);
    const LabelIcon = getLabelIcon(item.p_label);
    const labelStyle = getLabelStyle(item.p_label);

    return (
        <div className="bg-main-white rounded-xl border border-main-border overflow-hidden hover:border-main-blue/40 hover:shadow-lg transition-all group">
            <div className="relative p-5">
                {discount > 0 && (
                    <div className="absolute top-4 right-4 bg-main-red text-main-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-md">
                        <Sparkles size={11} />{discount}% OFF
                    </div>
                )}
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-main-blue rounded-lg flex items-center justify-center text-main-white font-bold text-lg shadow-md shadow-main-blue/25">
                        {item.p_name?.charAt(0)?.toUpperCase() || 'P'}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-main-font truncate">{item.p_name}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold mt-1.5 border ${labelStyle}`}>
                            <LabelIcon size={10} />{item.p_label}
                        </span>
                    </div>
                </div>
            </div>
            <div className="px-5 py-4 border-t border-main-border">
                <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-main-font">{formatPrice(item.p_price)}</span>
                    {item.p_main_price > item.p_price && <span className="text-sm text-main-subfont line-through">{formatPrice(item.p_main_price)}</span>}
                </div>
                {save > 0 && <p className="text-xs text-main-blue font-semibold mt-1 flex items-center gap-1"><TrendingUp size={12} />Save {formatPrice(save)}</p>}
                <div className="mt-3 p-3 bg-main-th border border-main-blue/20 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-main-exBlue/15 rounded-lg flex items-center justify-center"><CalendarClock size={16} className="text-main-exBlue" /></div>
                            <span className="text-xs font-medium text-main-subfont">Per Day</span>
                        </div>
                        <span className="text-sm font-bold text-main-exBlue">{formatPriceDecimal(perDay)}</span>
                    </div>
                </div>
                <div className="flex items-center gap-3 mt-3">
                    <span className="flex items-center gap-1 text-main-subfont text-sm"><Clock size={14} /><span className="font-medium">{item.p_duration} Days</span></span>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1 ${item.p_status ? 'bg-main-blue/10 text-main-blue' : 'bg-main-red/10 text-main-red'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${item.p_status ? 'bg-main-blue' : 'bg-main-red'}`}></span>
                        {item.p_status ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </div>
            {item.p_description && <div className="px-5 py-2.5 border-t border-main-border"><p className="text-xs text-main-subfont line-clamp-2">{item.p_description}</p></div>}
            <div className="px-5 py-3 flex items-center justify-between border-t border-main-border">
                <span className="text-xs text-main-subfont">{formatDate(item.createdAt)}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={() => onEdit(item)} className="p-2 text-main-exBlue hover:bg-main-exBlue/10 rounded-lg transition-colors" title="Edit"><Pencil size={16} /></button>
                    <button onClick={() => onDelete(item._id)} className="p-2 text-main-delete hover:bg-main-delete/10 rounded-lg transition-colors" title="Delete"><Trash2 size={16} /></button>
                </div>
            </div>
        </div>
    );
};

const TableRow = ({ item, index, formatPrice, formatPriceDecimal, calcDiscount, calcSave, getLabelIcon, getLabelStyle, onEdit, onDelete }) => {
    const discount = calcDiscount(item.p_main_price, item.p_price);
    const save = calcSave(item.p_main_price, item.p_price);
    const perDay = item.p_per_day || (item.p_price && item.p_duration ? (item.p_price / item.p_duration).toFixed(2) : 0);
    const LabelIcon = getLabelIcon(item.p_label);
    const labelStyle = getLabelStyle(item.p_label);

    return (
        <>
            <div className="hidden lg:grid lg:grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-main-thHover transition-colors group">
                <div className="col-span-1 text-sm font-medium text-main-subfont">{index + 1}</div>
                <div className="col-span-2 flex items-center gap-3">
                    <div className="w-10 h-10 bg-main-blue rounded-lg flex items-center justify-center text-main-white font-semibold shadow-sm flex-shrink-0">{item.p_name?.charAt(0)?.toUpperCase() || 'P'}</div>
                    <div className="min-w-0">
                        <p className="font-semibold text-main-font truncate">{item.p_name}</p>
                        {item.p_description && <p className="text-xs text-main-subfont truncate mt-0.5">{item.p_description}</p>}
                    </div>
                </div>
                <div className="col-span-2">
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-main-font">{formatPrice(item.p_price)}</span>
                        {discount > 0 && <span className="px-1.5 py-0.5 bg-main-red/10 text-main-red rounded text-xs font-bold">{discount}%</span>}
                    </div>
                    {save > 0 && <p className="text-xs text-main-subfont mt-0.5"><span className="line-through">{formatPrice(item.p_main_price)}</span><span className="text-main-blue font-medium ml-1">-{formatPrice(save)}</span></p>}
                </div>
                <div className="col-span-1">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-main-exBlue/10 border border-main-exBlue/20 rounded-lg">
                        <CalendarClock size={13} className="text-main-exBlue" />
                        <span className="text-xs font-bold text-main-exBlue">{formatPriceDecimal(perDay)}</span>
                    </div>
                </div>
                <div className="col-span-1 flex items-center gap-1 text-main-subfont text-sm"><Clock size={13} /><span className="font-medium">{item.p_duration}d</span></div>
                <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border ${labelStyle}`}><LabelIcon size={11} />{item.p_label}</span>
                </div>
                <div className="col-span-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${item.p_status ? 'bg-main-blue/10 text-main-blue' : 'bg-main-red/10 text-main-red'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${item.p_status ? 'bg-main-blue' : 'bg-main-red'}`}></span>
                        {item.p_status ? 'Active' : 'Inactive'}
                    </span>
                </div>
                <div className="col-span-1 text-xs text-main-subfont">{formatDate(item.createdAt)}</div>
                <div className="col-span-1 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={() => onEdit(item)} className="p-1.5 text-main-exBlue hover:bg-main-exBlue/10 rounded-lg transition-colors"><Pencil size={15} /></button>
                    <button onClick={() => onDelete(item._id)} className="p-1.5 text-main-delete hover:bg-main-delete/10 rounded-lg transition-colors"><Trash2 size={15} /></button>
                </div>
            </div>
            <div className="lg:hidden p-4 hover:bg-main-thHover transition-colors">
                <div className="flex gap-3">
                    <div className="w-12 h-12 bg-main-blue rounded-lg flex items-center justify-center text-main-white font-bold text-lg flex-shrink-0 shadow-sm">{item.p_name?.charAt(0)?.toUpperCase() || 'P'}</div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                            <div>
                                <h4 className="font-bold text-main-font">{item.p_name}</h4>
                                <div className="flex items-baseline gap-2 mt-1">
                                    <span className="text-lg font-bold text-main-font">{formatPrice(item.p_price)}</span>
                                    {discount > 0 && <span className="text-xs bg-main-red/10 text-main-red px-1.5 py-0.5 rounded font-bold">{discount}% OFF</span>}
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => onEdit(item)} className="p-1.5 text-main-exBlue"><Pencil size={17} /></button>
                                <button onClick={() => onDelete(item._id)} className="p-1.5 text-main-delete"><Trash2 size={17} /></button>
                            </div>
                        </div>
                        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-main-exBlue/10 border border-main-exBlue/20 rounded-lg">
                            <CalendarClock size={13} className="text-main-exBlue" />
                            <span className="text-xs font-bold text-main-exBlue">{formatPriceDecimal(perDay)}/day</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-xs text-main-subfont flex items-center gap-1"><Clock size={12} />{item.p_duration} Days</span>
                            <span className={`text-xs px-2 py-0.5 rounded font-semibold flex items-center gap-1 ${item.p_status ? 'bg-main-blue/10 text-main-blue' : 'bg-main-red/10 text-main-red'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${item.p_status ? 'bg-main-blue' : 'bg-main-red'}`}></span>
                                {item.p_status ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const Modal = ({ show, onClose, title, icon: Icon, children }) => (
    <AnimatePresence>
        {show && (
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <div className="fixed inset-0 bg-main-font/50 backdrop-blur-sm" onClick={onClose}></div>
                <motion.div
                    className="relative bg-main-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border border-main-border"
                    initial={{ scale: 0.95, y: 12, opacity: 0 }}
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.95, y: 12, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="flex items-center justify-between p-5 border-b border-main-border bg-main-th">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-main-blue rounded-lg flex items-center justify-center shadow-md shadow-main-blue/25"><Icon size={16} className="text-main-white" /></div>
                            <h3 className="text-lg font-bold text-main-font">{title}</h3>
                        </div>
                        <button onClick={onClose} className="w-8 h-8 hover:bg-main-white rounded-lg flex items-center justify-center transition-colors"><X size={18} className="text-main-subfont" /></button>
                    </div>
                    <div className="p-5 overflow-y-auto max-h-[calc(90vh-80px)]">{children}</div>
                </motion.div>
            </motion.div>
        )}
    </AnimatePresence>
);

const PlanForm = ({ formData, formErrors, isSubmitting, onChange, onStatusToggle, onSubmit, onCancel, submitLabel, formatPrice, formatPriceDecimal, calcDiscount, calcSave, calcPerDay }) => {
    const discount = calcDiscount(formData.p_main_price, formData.p_price);
    const save = calcSave(formData.p_main_price, formData.p_price);
    const perDay = calcPerDay(formData.p_price, formData.p_duration);

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            <FormInput label="Plan Name" name="p_name" value={formData.p_name} onChange={onChange} placeholder="e.g., Premium, Basic, Pro" error={formErrors.p_name} icon={Hash} />
            <div className="grid grid-cols-2 gap-3">
                <FormInput label="MRP Price" name="p_main_price" value={formData.p_main_price} onChange={onChange} placeholder="999" error={formErrors.p_main_price} icon={IndianRupee} />
                <FormInput label="Offer Price" name="p_price" value={formData.p_price} onChange={onChange} placeholder="499" error={formErrors.p_price} icon={IndianRupee} />
            </div>
            {(discount > 0 || Number(perDay) > 0) && (
                <div className="p-4 bg-main-th border border-main-blue/20 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-main-blue rounded-lg flex items-center justify-center shadow-sm"><TrendingUp size={14} className="text-main-white" /></div>
                        <span className="text-sm font-bold text-main-font">Plan Preview</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {discount > 0 && (
                            <div className="bg-main-white rounded-lg p-3 border border-main-border">
                                <p className="text-xs text-main-subfont font-medium mb-0.5">Discount</p>
                                <p className="text-lg font-bold text-main-red">{discount}% OFF</p>
                            </div>
                        )}
                        {save > 0 && (
                            <div className="bg-main-white rounded-lg p-3 border border-main-border">
                                <p className="text-xs text-main-subfont font-medium mb-0.5">Customer Saves</p>
                                <p className="text-lg font-bold text-main-blue">{formatPrice(save)}</p>
                            </div>
                        )}
                        {Number(perDay) > 0 && (
                            <div className={`bg-main-exBlue/10 rounded-lg p-3 border border-main-exBlue/20 ${discount > 0 && save > 0 ? 'col-span-2' : ''}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-main-subfont font-medium mb-0.5">Per Day Cost</p>
                                        <p className="text-lg font-bold text-main-exBlue">{formatPriceDecimal(perDay)}</p>
                                    </div>
                                    <div className="w-10 h-10 bg-main-exBlue/15 rounded-lg flex items-center justify-center"><CalendarClock size={20} className="text-main-exBlue" /></div>
                                </div>
                                <p className="text-xs text-main-subfont mt-1.5">= {formatPrice(formData.p_price)} ÷ {formData.p_duration} days</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className="grid grid-cols-2 gap-3">
                <FormInput label="Duration (Days)" name="p_duration" value={formData.p_duration} onChange={onChange} placeholder="30" error={formErrors.p_duration} icon={Clock} />
                <FormInput label="Label" name="p_label" value={formData.p_label} onChange={onChange} placeholder="Popular, Premium" error={formErrors.p_label} icon={Tag} />
            </div>
            <div>
                <label className="block text-sm font-semibold text-main-font mb-1.5">Description <span className="text-main-subfont font-normal">(Optional)</span></label>
                <TextField
                    name="p_description"
                    value={formData.p_description}
                    onChange={onChange}
                    placeholder="Brief description of the plan features..."
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                    sx={fieldSx}
                />
            </div>
            <div className="flex items-center justify-between p-3.5 bg-main-bg border border-main-border rounded-lg">
                <div>
                    <p className="font-semibold text-main-font text-sm">Plan Status</p>
                    <p className="text-xs text-main-subfont mt-0.5">Enable or disable this plan</p>
                </div>
                <div onClick={onStatusToggle} className={`relative w-12 h-6 rounded-full cursor-pointer transition-all ${formData.p_status ? 'bg-main-blue' : 'bg-main-border'}`}>
                    <span className={`absolute top-0.5 w-5 h-5 bg-main-white rounded-full shadow-md transition-all ${formData.p_status ? 'left-6' : 'left-0.5'}`} />
                </div>
            </div>
            <div className="flex gap-3 pt-2">
                <button type="button" onClick={onCancel} className="flex-1 px-4 py-2.5 border border-main-border text-main-font rounded-lg hover:bg-main-bg font-semibold text-sm transition-all">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2.5 bg-main-blue text-main-white rounded-lg font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-main-blue/30 transition-all active:scale-[0.98]">
                    {isSubmitting ? (<><CircularProgress size={16} color="inherit" />Processing...</>) : (<><Check size={16} strokeWidth={2.5} />{submitLabel}</>)}
                </button>
            </div>
        </form>
    );
};

const FormInput = ({ label, name, value, onChange, placeholder, error, icon: Icon }) => (
    <div>
        <label className="block text-sm font-semibold text-main-font mb-1.5">{label} <span className="text-main-red">*</span></label>
        <TextField
            fullWidth
            size="small"
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            error={!!error}
            helperText={error || ''}
            sx={fieldSx}
            slotProps={{
                input: {
                    startAdornment: Icon ? (
                        <InputAdornment position="start">
                            <Icon size={17} className="text-main-subfont" />
                        </InputAdornment>
                    ) : undefined,
                },
            }}
        />
    </div>
);

const SortIcon = ({ field, sortField, sortOrder }) => {
    if (sortField !== field) return <div className="flex flex-col ml-1 opacity-30"><ChevronUp size={10} className="-mb-0.5" /><ChevronDown size={10} /></div>;
    return sortOrder === 'asc' ? <ChevronUp size={14} className="ml-1 text-main-blue" /> : <ChevronDown size={14} className="ml-1 text-main-blue" />;
};

const SortableHeader = ({ field, label, handleSort, sortField, sortOrder }) => (
    <div className="flex items-center cursor-pointer hover:text-main-blue transition-colors select-none" onClick={() => handleSort(field)}>
        <span>{label}</span><SortIcon field={field} sortField={sortField} sortOrder={sortOrder} />
    </div>
);
